import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { CIO_EVENT_NAMES } from "./events";

/**
 * Storefront event ingestion. Public on purpose (anonymous visitors browse
 * the catalogue), but strictly validated: only known event names, bounded
 * payload, and nothing privileged is returned. Credentials stay server-side.
 */
const payloadSchema = z
  .record(z.string().max(64), z.union([z.string().max(500), z.number(), z.boolean(), z.null()]))
  .refine((p) => Object.keys(p).length <= 40, "payload too large");

export const trackStorefrontEvent = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        name: z.string().refine((n) => (CIO_EVENT_NAMES as string[]).includes(n), "unknown event"),
        payload: payloadSchema.default({}),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    try {
      // While the Razorpay Test Mode switch is on, storefront events never reach journeys.
      const { resolveRequestPaymentMode } = await import("@/lib/payments/mode-switch.server");
      if ((await resolveRequestPaymentMode()).mode === "test") return { ok: true, skipped: true };
      const { cioTrack } = await import("./client.server");
      const res = await cioTrack(data.name, data.payload);
      return { ok: res.ok, skipped: !!res.skipped };
    } catch {
      return { ok: false, skipped: true };
    }
  });

export const identifyStorefrontCustomer = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) =>
    z
      .object({
        email: z.string().email().max(255),
        customerId: z.string().max(128).optional(),
        attributes: payloadSchema.default({}),
      })
      .parse(input),
  )
  .handler(async ({ data }) => {
    try {
      const { syncBasicProfile } = await import("./profile.server");
      const res = await syncBasicProfile(data.email, {
        ...data.attributes,
        customer_id: data.customerId,
      });
      return { ok: res.ok };
    } catch {
      return { ok: false };
    }
  });

/* ------------------------------ Admin surface ----------------------------- */

export const cioDiagnostics = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const mod = await import("./customerio.server");
    await mod.assertElevated(context);
    return mod.getDiagnostics();
  });

export const cioListLogs = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z
      .object({
        search: z.string().max(120).optional(),
        eventName: z.string().max(80).optional(),
        status: z.enum(["sent", "failed", "skipped", "pending"]).optional(),
        limit: z.number().int().min(1).max(500).optional(),
      })
      .parse(input ?? {}),
  )
  .handler(async ({ data, context }) => {
    const mod = await import("./customerio.server");
    await mod.assertElevated(context);
    return mod.listLogs(data);
  });

export const cioSendTestEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) =>
    z.object({ email: z.string().email().max(255), emailFlavour: z.boolean().default(false) }).parse(input),
  )
  .handler(async ({ data, context }) => {
    const mod = await import("./customerio.server");
    await mod.assertElevated(context);
    return mod.sendTestEvent(data.email, data.emailFlavour);
  });

export const cioSyncCustomer = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: unknown) => z.object({ email: z.string().email().max(255) }).parse(input))
  .handler(async ({ data, context }) => {
    const mod = await import("./customerio.server");
    await mod.assertElevated(context);
    const res = await mod.syncCustomerProfile(data.email);
    return { ok: res.ok, error: res.error ?? null };
  });

export const cioSyncAllCustomers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const mod = await import("./customerio.server");
    await mod.assertElevated(context);
    return mod.syncAllCustomerProfiles();
  });

export const cioRetryFailed = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const mod = await import("./customerio.server");
    await mod.assertElevated(context);
    return mod.retryFailedEvents();
  });